// #/admin v1 — the operator's door (campaign T57 WP2).
//
// Three panels: the provider list with per-type completeness, the claim-code
// issuer (with a printable QR), and the application approval queue.
//
// The guard here is UX only. Every write behind it is authorised server-side:
// admin_list_providers and issue_provider_claim_code raise
// insufficient_privilege for a non-superAdmin, and provider_applications has
// superAdmin-only RLS for read and update. A tampered client gets errors, not
// data — which is why the page can afford to fail closed and simply say so.

import { requireSuperAdmin, NotSuperAdminError } from '../lib/operator-guard';
import { getOptionalSession, signOut } from '../lib/auth-guard';
import { rememberReturnTo } from '../lib/return-to';
import {
  adminListProviders,
  issueClaimCode,
  listPendingApplications,
  setApplicationStatus,
  type AdminProviderRow,
  type ApplicationRow,
} from '../lib/admin-api';
import { qrPngDataUrl, claimUrl } from '../lib/qr';
import {
  listReviewQueue,
  reviewMenuRow,
  euroTextToCents,
  centsToEuroText,
  type ReviewQueueRow,
} from '../lib/menu-api';
import { renderCreatorPicks } from './creator-picks';
import { CANONICAL_ORIGIN } from '../lib/canonical';
import { supabase } from '../lib/supabase';
import {
  missingFor, PER_TYPE_COLUMNS, BOOKING_PLATFORMS, PLATFORM_ID_COLUMN,
  type PerTypeProfile,
} from '../lib/per-type';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

/** Completeness columns, in the order the brief lists them. */
const COLUMNS: ReadonlyArray<readonly [keyof AdminProviderRow, string, string]> = [
  ['has_hours', 'Hours', 'Opening hours set'],
  ['has_logo', 'Logo', 'Logo uploaded'],
  ['has_cover', 'Cover', 'Cover or banner image'],
  ['has_photos', 'Photos', 'At least one gallery photo'],
  ['has_phone', 'Phone', 'Phone number'],
  ['has_booking', 'Booking', 'Booking link or platform'],
];

export async function renderAdmin(container: HTMLElement): Promise<void> {
  try {
    await requireSuperAdmin();
  } catch (err) {
    if (err instanceof NotSuperAdminError) {
      // Say WHICH account is signed in, and offer a way out. John hit this
      // because Safari still held a stale test-provider session from an earlier
      // walk, and the old message gave him no way to tell — "operator only" is
      // useless when the real problem is that you are logged in as someone else.
      const session = await getOptionalSession();
      const who = session?.user.email ?? 'an account we cannot name';
      container.innerHTML = `
        <main class="container narrow">
          <h1>Operator admin</h1>
          <p class="status status-error">This area is for the Curate operator only.</p>
          <p>You are signed in as <strong>${esc(who)}</strong>.</p>
          <p>
            <button type="button" class="ad-mini" id="ad-signout">Sign out and use another account</button>
          </p>
          <p><a href="#/edit">Or go to that account's profile →</a></p>
        </main>`;
      container.querySelector<HTMLButtonElement>('#ad-signout')
        ?.addEventListener('click', () => {
          // Send them to the operator sign-in rather than the home page, so the
          // next link they request comes back here.
          rememberReturnTo('#/admin');
          void signOut();
        });
    }
    return;  // requireSession already redirected to #/signin when signed out
  }

  container.innerHTML = `
    <main class="container ad-wrap">
      <h1>Operator admin</h1>

      <nav class="ad-tabs" role="tablist">
        <button class="ad-tab ad-on" data-panel="providers" role="tab">Providers</button>
        <button class="ad-tab" data-panel="applications" role="tab">Applications</button>
        <button class="ad-tab" data-panel="menus" role="tab">Menu review</button>
      </nav>

      <section id="ad-providers" class="ad-panel ad-show">
        <div class="ad-toolbar">
          <input type="search" id="ad-q" placeholder="Filter by business name…" autocomplete="off" />
          <span class="ad-counts" id="ad-counts"></span>
        </div>
        <div class="ad-tablewrap"><table class="ad-table">
          <thead><tr>
            <th>Business</th><th>Type</th><th>Claimed</th>
            ${COLUMNS.map(([, label, title]) => `<th title="${esc(title)}">${esc(label)}</th>`).join('')}
            <th title="Menu rows awaiting John's review">Menu</th>
            <th title="Mechanic service type set and profile active">SOS</th>
            <th title="Per-type fields still blank (WP4)">Missing</th>
            <th title="Booking platform + ID — operator-only (John's ruling 2026-09-05)">Booking</th>
            <th>Claim code</th>
          </tr></thead>
          <tbody id="ad-rows"><tr><td colspan="12">Loading…</td></tr></tbody>
        </table></div>
      </section>

      <section id="ad-applications" class="ad-panel">
        <div id="ad-apps">Loading…</div>
      </section>

      <section id="ad-menus" class="ad-panel">
        <p class="ad-muted">Menu rows read from provider photos. Nothing here is live until you
          approve it. Correcting a price leaves the row waiting — editing is not approving.</p>
        <div id="ad-menu-queue">Loading…</div>
      </section>

      <p id="ad-status" class="status" aria-live="polite"></p>
      <div id="ad-qr" class="ad-qr" hidden></div>
    </main>
  `;

  const q = <T extends HTMLElement>(sel: string): T => container.querySelector<T>(sel)!;
  const status = q<HTMLElement>('#ad-status');
  const setStatus = (msg: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = msg;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  container.querySelectorAll<HTMLButtonElement>('.ad-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      container.querySelectorAll('.ad-tab').forEach((t) => t.classList.remove('ad-on'));
      container.querySelectorAll('.ad-panel').forEach((p) => p.classList.remove('ad-show'));
      tab.classList.add('ad-on');
      q<HTMLElement>(`#ad-${tab.dataset.panel}`).classList.add('ad-show');
    });
  });

  // ── Providers ──
  const tick = (on: boolean): string =>
    on ? '<span class="ad-y" aria-label="yes">●</span>' : '<span class="ad-n" aria-label="no">○</span>';

  // Per-type gaps, keyed by profile id. Computed here rather than added to
  // admin_list_providers so that RPC stays byte-identical while it is under
  // review; superAdmins can already select these columns directly.
  let perTypeById = new Map<string, PerTypeProfile>();
  const loadPerType = async (): Promise<void> => {
    const { data, error } = await supabase
      .from('provider_profiles')
      .select(`id, ${PER_TYPE_COLUMNS}`)
      .limit(500);
    if (error) return;   // the Missing column just stays blank
    perTypeById = new Map(
      (data ?? []).map((r) => [
        String((r as unknown as { id: string }).id),
        r as unknown as PerTypeProfile,
      ]),
    );
  };

  /**
   * The booking cell. The platform ID is operator-only since John's ruling — a
   * provider can pick the platform and give their own link, but the ID that names
   * a record in someone else's commerce system is what makes the pipeline tell a
   * traveller "[Direct booking available]", so the operator sets it.
   *
   * Write path: a plain PostgREST update on provider_profiles. Nothing new is
   * needed — the "Owners and superAdmins update provider profiles" policy lets a
   * superAdmin through, and protect_provider_profile_columns early-returns for a
   * superAdmin before any revert runs. Both predate this campaign and both are
   * asserted in proofs/booking_ids_operator_only_proof.sql (O1/O2).
   */
  const bookingCell = (id: string): string => {
    const row = perTypeById.get(id);
    if (!row) return '<span class="ad-muted">—</span>';
    const column = PLATFORM_ID_COLUMN[row.booking_platform ?? ''] ?? null;
    const idValue = column ? ((row[column] as string | null) ?? '') : '';
    const label = !row.booking_platform ? 'set'
      : column ? (idValue ? `${row.booking_platform} ✓` : `${row.booking_platform} — no ID`)
      : `${row.booking_platform} (link only)`;
    return `<button type="button" class="ad-mini" data-booking="${esc(id)}">${esc(label)}</button>`;
  };

  const openBookingEditor = (id: string): void => {
    const row = perTypeById.get(id);
    if (!row) return;
    const panel = q<HTMLElement>('#ad-qr');   // reuse the modal shell
    panel.hidden = false;
    const column = PLATFORM_ID_COLUMN[row.booking_platform ?? ''] ?? null;
    panel.innerHTML = `
      <div class="ad-qr-card ad-booking-card">
        <button type="button" class="ad-qr-close" id="ad-bk-close" aria-label="Close">×</button>
        <h2>Booking</h2>
        <label for="ad-bk-platform">Platform</label>
        <select id="ad-bk-platform">
          ${BOOKING_PLATFORMS.map(([v, l]) =>
            `<option value="${esc(v)}"${(row.booking_platform ?? '') === v ? ' selected' : ''}>${esc(l)}</option>`).join('')}
        </select>
        <label for="ad-bk-id">Platform ID <span class="ad-muted">(operator-only)</span></label>
        <input type="text" id="ad-bk-id" autocomplete="off"
               value="${esc(column ? ((row[column] as string | null) ?? '') : '')}" />
        <label for="ad-bk-url">Booking link</label>
        <input type="url" id="ad-bk-url" value="${esc(row.external_booking_url)}" />
        <p class="ad-muted">Setting an ID makes the tour pipeline tell travellers this
          provider is directly bookable. Only set one you have verified.</p>
        <button type="button" class="ad-mini" id="ad-bk-save">Save booking</button>
      </div>`;

    panel.querySelector<HTMLButtonElement>('#ad-bk-close')!.addEventListener('click', () => {
      panel.hidden = true; panel.innerHTML = '';
    });
    panel.querySelector<HTMLButtonElement>('#ad-bk-save')!.addEventListener('click', async () => {
      const platform = panel.querySelector<HTMLSelectElement>('#ad-bk-platform')!.value;
      const rawId = panel.querySelector<HTMLInputElement>('#ad-bk-id')!.value.trim();
      const url = panel.querySelector<HTMLInputElement>('#ad-bk-url')!.value.trim();
      const patch: Record<string, unknown> = {
        booking_platform: platform === '' ? null : platform,
        external_booking_url: url === '' ? null : url,
        // Clear every ID column, then set the one this platform owns, so
        // switching platform cannot leave a stale ID claiming bookability
        // somewhere the provider no longer uses.
        thefork_restaurant_id: null, bokun_activity_id: null,
        viator_product_code: null, booking_com_property_id: null,
      };
      const col = PLATFORM_ID_COLUMN[platform] ?? null;
      if (col) patch[col] = rawId === '' ? null : rawId;

      const { error } = await supabase.from('provider_profiles').update(patch).eq('id', id);
      if (error) { setStatus(error.message, 'error'); return; }
      setStatus('Booking saved.', 'success');
      panel.hidden = true; panel.innerHTML = '';
      perTypeById.set(id, { ...row, ...(patch as Partial<PerTypeProfile>) } as PerTypeProfile);
      await load(q<HTMLInputElement>('#ad-q').value);
    });
  };

  const missingCell = (id: string): string => {
    const row = perTypeById.get(id);
    if (!row) return '<span class="ad-muted">—</span>';
    const left = missingFor(row);
    if (left.length === 0) return '<span class="ad-y">●</span>';
    return `<span class="ad-missing" title="${esc(left.map((m) => m.label).join(', '))}">${
      esc(left.map((m) => m.label).join(', '))}</span>`;
  };

  const renderRows = (rows: AdminProviderRow[]): void => {
    q<HTMLElement>('#ad-counts').textContent =
      `${rows.length} shown · ${rows.filter((r) => r.claimed_at).length} claimed · ` +
      `${rows.filter((r) => r.menus_unreviewed > 0).length} with menus to review`;

    q<HTMLElement>('#ad-rows').innerHTML = rows.length === 0
      ? '<tr><td colspan="14">No matches.</td></tr>'
      : rows.map((r) => `
        <tr${r.is_active ? '' : ' class="ad-inactive"'}>
          <td>${esc(r.business_name)}${r.is_active ? '' : ' <em>(inactive)</em>'}</td>
          <td>${esc(r.business_type)}</td>
          <td>${r.claimed_at ? tick(true) : tick(false)}</td>
          ${COLUMNS.map(([key]) => `<td>${tick(Boolean(r[key]))}</td>`).join('')}
          <td>${r.menus_total === 0 ? tick(false)
                : `${r.menus_total}${r.menus_unreviewed > 0 ? ` <strong>(${r.menus_unreviewed} new)</strong>` : ''}`}</td>
          <td>${tick(r.sos_eligible)}</td>
          <td>${missingCell(r.id)}</td>
          <td>${bookingCell(r.id)}</td>
          <td>${r.claimed_at
                ? '<span class="ad-muted">—</span>'
                : `<button type="button" class="ad-mini" data-issue="${esc(r.id)}">Issue${
                     r.open_code_count > 0 ? ` (${r.open_code_count} open)` : ''}</button>`}</td>
        </tr>`).join('');

    q<HTMLElement>('#ad-rows').querySelectorAll<HTMLButtonElement>('button[data-issue]')
      .forEach((btn) => btn.addEventListener('click', () => void issue(btn.dataset.issue!)));
    q<HTMLElement>('#ad-rows').querySelectorAll<HTMLButtonElement>('button[data-booking]')
      .forEach((btn) => btn.addEventListener('click', () => openBookingEditor(btn.dataset.booking!)));
  };

  const load = async (query?: string): Promise<void> => {
    try {
      if (perTypeById.size === 0) await loadPerType();
      renderRows(await adminListProviders(query));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not load providers.', 'error');
    }
  };

  const issue = async (profileId: string): Promise<void> => {
    setStatus('Issuing a code…', '');
    try {
      const issued = await issueClaimCode(profileId);
      const url = claimUrl(CANONICAL_ORIGIN, issued.code);
      const png = qrPngDataUrl(url);
      const panel = q<HTMLElement>('#ad-qr');
      panel.hidden = false;
      panel.innerHTML = `
        <div class="ad-qr-card">
          <button type="button" class="ad-qr-close" id="ad-qr-close" aria-label="Close">×</button>
          <h2>${esc(issued.business_name)}</h2>
          <p class="ad-code">${esc(issued.code)}</p>
          <img src="${png}" alt="QR code linking to ${esc(url)}" width="240" height="240" />
          <p class="ad-qr-url">${esc(url)}</p>
          <p class="ad-muted">Single use · expires ${esc(new Date(issued.expires_at).toLocaleDateString())}</p>
          <a class="ad-mini" download="curate-claim-${esc(issued.code)}.png" href="${png}">Download PNG</a>
        </div>`;
      panel.querySelector<HTMLButtonElement>('#ad-qr-close')!.addEventListener('click', () => {
        panel.hidden = true;
        panel.innerHTML = '';
      });
      setStatus(`Code ${issued.code} issued for ${issued.business_name}.`, 'success');
      await load(q<HTMLInputElement>('#ad-q').value);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not issue a code.', 'error');
    }
  };

  let debounce: number | undefined;
  q<HTMLInputElement>('#ad-q').addEventListener('input', (e) => {
    const term = (e.target as HTMLInputElement).value;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void load(term), 220);
  });

  // ── Applications ──
  const renderApps = (apps: ApplicationRow[]): void => {
    q<HTMLElement>('#ad-apps').innerHTML = apps.length === 0
      ? '<p class="ad-muted">Nothing pending.</p>'
      : `<ul class="ad-apps">${apps.map((a) => `
          <li>
            <div>
              <strong>${esc(a.business_name)}</strong>
              <span>${esc(a.provider_type)} · ${esc(a.contact_email)}</span>
              <span class="ad-muted">${a.user_id ? 'signed in when applying' : 'anonymous — will need a claim code'}</span>
            </div>
            <div class="ad-app-actions">
              <button type="button" class="ad-mini" data-approve="${esc(a.id)}">Approve</button>
              <button type="button" class="ad-mini ad-danger" data-reject="${esc(a.id)}">Reject</button>
            </div>
          </li>`).join('')}</ul>`;

    const wire = (attr: string, next: 'approved' | 'rejected'): void => {
      q<HTMLElement>('#ad-apps').querySelectorAll<HTMLButtonElement>(`button[data-${attr}]`)
        .forEach((btn) => btn.addEventListener('click', async () => {
          btn.disabled = true;
          try {
            await setApplicationStatus(btn.dataset[attr]!, next);
            setStatus(`Application ${next}.`, 'success');
            await loadApps();
            if (next === 'approved') await load(q<HTMLInputElement>('#ad-q').value);
          } catch (err) {
            btn.disabled = false;
            setStatus(err instanceof Error ? err.message : `Could not ${next} it.`, 'error');
          }
        }));
    };
    wire('approve', 'approved');
    wire('reject', 'rejected');
  };

  const loadApps = async (): Promise<void> => {
    try {
      renderApps(await listPendingApplications());
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not load applications.', 'error');
    }
  };

  // ── Menu review queue (WP3) ──
  const renderQueue = (rows: ReviewQueueRow[]): void => {
    if (rows.length === 0) {
      q<HTMLElement>('#ad-menu-queue').innerHTML = '<p class="ad-muted">Nothing waiting for review.</p>';
      return;
    }
    q<HTMLElement>('#ad-menu-queue').innerHTML = `
      <div class="ad-tablewrap"><table class="ad-table ad-menu-table">
        <thead><tr>
          <th>Business</th><th>Section</th><th>Item</th><th>Price €</th><th>Photo</th><th></th>
        </tr></thead>
        <tbody>${rows.map((r) => `
          <tr data-row="${esc(r.id)}">
            <td>${esc(r.business_name)}</td>
            <td>${esc(r.section) || '<span class="ad-muted">—</span>'}</td>
            <td>${esc(r.item)}${r.description ? `<div class="ad-muted">${esc(r.description)}</div>` : ''}</td>
            <td><input type="text" inputmode="decimal" class="ad-price" size="7"
                       value="${esc(centsToEuroText(r.price_cents))}"
                       aria-label="Price in euro for ${esc(r.item)}" /></td>
            <td>${r.source_photo_url
                  ? `<a href="${esc(r.source_photo_url)}" target="_blank" rel="noopener noreferrer">view</a>`
                  : '<span class="ad-muted">—</span>'}</td>
            <td class="ad-app-actions">
              <button type="button" class="ad-mini" data-menu-approve="${esc(r.id)}">Approve</button>
              <button type="button" class="ad-mini" data-menu-edit="${esc(r.id)}">Save price</button>
              <button type="button" class="ad-mini ad-danger" data-menu-reject="${esc(r.id)}">Reject</button>
            </td>
          </tr>`).join('')}</tbody>
      </table></div>`;

    const priceInputFor = (id: string): string =>
      q<HTMLElement>('#ad-menu-queue')
        .querySelector<HTMLInputElement>(`tr[data-row="${CSS.escape(id)}"] .ad-price`)
        ?.value.trim() ?? '';

    const priceFor = (id: string): number | null => {
      const raw = priceInputFor(id);
      return raw === '' ? null : euroTextToCents(raw);
    };

    /** The row's existing currency, defaulting to EUR only when it has none. */
    const currencyFor = (id: string): string =>
      rows.find((r) => r.id === id)?.currency ?? 'EUR';

    const act = (attr: string, action: 'approve' | 'reject' | 'edit'): void => {
      q<HTMLElement>('#ad-menu-queue')
        .querySelectorAll<HTMLButtonElement>(`button[data-${attr}]`)
        .forEach((btn) => btn.addEventListener('click', async () => {
          const id = btn.dataset[attr.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())] ?? '';
          btn.disabled = true;
          try {
            // A price typed into the row rides along with approve, so the
            // operator can correct and approve in one action.
            //
            // N22: a price box that has text in it but does not parse used to be
            // silently ignored, and `approve` then approved the row at its OLD
            // price. Refusing to guess is right; saying nothing is not.
            const typed = priceInputFor(id);
            const cents = action === 'reject' ? null : priceFor(id);
            if (action !== 'reject' && typed !== '' && cents === null) {
              btn.disabled = false;
              setStatus(`"${typed}" is not a price I can read — try 9,50 or 9.50.`, 'error');
              return;
            }
            const result = await reviewMenuRow(id, action,
              // N21: only send a currency when we are actually setting a price,
              // and reuse the row's own currency — hard-coding EUR silently
              // rewrote the currency of any non-EUR row the operator touched.
              cents === null ? undefined : { cents, currency: currencyFor(id) });
            const verb = action === 'edit' ? 'updated'
              : action === 'approve' ? 'approved'
              : 'rejected';          // N20: `action + 'd'` rendered "Rejectd"
            setStatus(result.ok
              ? `Row ${verb}.`
              : `Refused: ${result.reason ?? 'unknown'}`, result.ok ? 'success' : 'error');
            await loadQueue();
          } catch (err) {
            btn.disabled = false;
            setStatus(err instanceof Error ? err.message : 'Could not save that.', 'error');
          }
        }));
    };
    act('menu-approve', 'approve');
    act('menu-reject', 'reject');
    act('menu-edit', 'edit');
  };

  const loadQueue = async (): Promise<void> => {
    try {
      renderQueue(await listReviewQueue('unreviewed'));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not load the review queue.', 'error');
    }
  };

  await load();
  await loadApps();
  await loadQueue();

  // ── Creator picks (T57 WP0-B) ──
  // Mounted after the panels above and behind the same guard: `requireSuperAdmin`
  // has already returned by the time we get here. It appends its own section and
  // its own tab; nothing else in this file changes.
  await renderCreatorPicks(container);
}
